import * as React from "react";
import { Button, Col, Row, SegmentedControl } from "components/bootstrap";
import { FetchErrorAlert, RelativeTime, Spinner } from "components/common";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import HealthDetailsPane from "./HealthDetailsPane";
import HealthInterpretationLegend from "./HealthInterpretationLegend";
import HealthTreePane from "./HealthTreePane";
import useHealthPanel from "./useHealthPanel";
import {
  BodyText,
  EmptyStatePane,
  EmptyStateTitle,
  ModuleContent,
  ModuleHeader,
  ModuleHeaderActions,
  ModuleHeaderMeta,
  ModuleHeaderText,
  ModuleLayout,
  ModuleState,
  ModuleTitle
} from "./HealthModule.styles";
const REPORT_VIEW_OPTIONS = [
  { value: "problems", label: "Problems only" },
  { value: "all", label: "All checks" }
];
const AllHealthyEmptyState = ({ onShowAllChecks }) => /* @__PURE__ */ React.createElement(EmptyStatePane, null, /* @__PURE__ */ React.createElement(EmptyStateTitle, null, "All checks are healthy"), /* @__PURE__ */ React.createElement(BodyText, null, "The current report has no warning, critical, or unknown checks."), /* @__PURE__ */ React.createElement(Button, { bsStyle: "link", bsSize: "small", onClick: onShowAllChecks }, "Show all checks"));
const HealthModule = () => {
  const {
    showHealthModule,
    viewMode,
    setViewMode,
    problemsOnly,
    error,
    generatedAt,
    hasReport,
    isFetching,
    isLoading,
    lookup,
    root,
    treeData,
    tree,
    selectedNode,
    selectedPath,
    isRootSelected,
    hasNoVisibleProblems,
    hasHardFailure
  } = useHealthPanel();
  const sendTelemetry = useSendTelemetry("cluster-health");
  if (!showHealthModule) return null;
  const fetchErrorMessage = hasReport ? "Refreshing cluster health report failed. Showing last successful report" : "Fetching cluster health report failed";
  const changeViewMode = (mode) => {
    setViewMode(mode);
    sendTelemetry(TELEMETRY_EVENT_TYPE.CLUSTER_HEALTH.VIEW_MODE_TOGGLED, {
      app_action_value: mode
    });
  };
  const renderDetailsContent = () => {
    if (hasNoVisibleProblems) {
      return /* @__PURE__ */ React.createElement(AllHealthyEmptyState, { onShowAllChecks: () => changeViewMode("all") });
    }
    if (isRootSelected) {
      return /* @__PURE__ */ React.createElement(HealthInterpretationLegend, null);
    }
    return /* @__PURE__ */ React.createElement(
      HealthDetailsPane,
      {
        tree,
        selectedNode,
        selectedPath,
        showHealthyChildren: !problemsOnly
      }
    );
  };
  return /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(ModuleHeader, null, /* @__PURE__ */ React.createElement(ModuleHeaderText, null, /* @__PURE__ */ React.createElement(ModuleTitle, null, "Deployment Health"), generatedAt ? /* @__PURE__ */ React.createElement(ModuleHeaderMeta, null, "Last generated ", /* @__PURE__ */ React.createElement(RelativeTime, { dateTime: generatedAt }), isFetching ? " - Refreshing..." : null) : null), /* @__PURE__ */ React.createElement(ModuleHeaderActions, { role: "group", "aria-label": "Health report view" }, /* @__PURE__ */ React.createElement(
    SegmentedControl,
    {
      data: REPORT_VIEW_OPTIONS,
      radius: "sm",
      value: viewMode,
      onChange: changeViewMode
    }
  ))), /* @__PURE__ */ React.createElement(ModuleContent, null, isLoading ? /* @__PURE__ */ React.createElement(ModuleState, null, /* @__PURE__ */ React.createElement(Spinner, { text: "Loading cluster health report...", delay: 0 })) : /* @__PURE__ */ React.createElement(React.Fragment, null, error ? /* @__PURE__ */ React.createElement(FetchErrorAlert, { message: fetchErrorMessage, error }) : null, hasHardFailure ? null : /* @__PURE__ */ React.createElement(ModuleLayout, null, /* @__PURE__ */ React.createElement(HealthTreePane, { tree, treeData, lookup, root, viewMode }), renderDetailsContent())))));
};
export default HealthModule;
